import { CalendarOutlined, MessageOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { BillReservationTag } from "../../../../../api/bills";
import { formatClock } from "../../../../reservations/logic/dayTime";
import { useSeatFormat } from "./useSeatFormat";

// Лента „настанена резервация" — над списъка с поръчаното (OrderDetails), по
// образеца на лентата на закачения клиент (BillCustomerBanner): водещ бадж
// (календарчето — същият знак като на картата на масата) · името на гостите ·
// часът, гостите и масите; забележката от резервацията (повод, алергии) — на
// свой ред, защото е важна за сервитьора. Телефонът не идва от BE нарочно.

interface BillReservationBannerProps {
  reservation: BillReservationTag;
  /** Тесен екран — по-дребни кегели и отстъпи. */
  compact?: boolean;
  /** Хоризонталният падинг го дава РОДИТЕЛЯТ — подравнен с колоните под лентата. */
  className?: string;
}

export const BillReservationBanner = ({
  reservation,
  compact = false,
  className = "",
}: BillReservationBannerProps) => {
  const { t } = useTranslation();
  const format = useSeatFormat();
  const details = [
    `${formatClock(Date.parse(reservation.start_at))}–${formatClock(Date.parse(reservation.end_at))}`,
    t("reservations.counts.guests", { count: reservation.guests }),
    format.tables(reservation.table_names),
  ].join(" · ");

  return (
    <div
      // Лента на цяла ширина: без рамка/радиус, само долна линия към
      // съдържанието под нея — като лентата на клиента.
      className={`flex items-center border-b border-pos-line2 bg-pos-marked text-pos-ink ${
        compact ? "gap-2.5 py-1" : "gap-3 py-[5px]"
      } ${className}`}
    >
      <span
        className={`inline-flex shrink-0 items-center justify-center rounded-lg border border-pos-line bg-pos-card text-pos-accent ${
          compact ? "size-6 text-[12px]" : "size-7 text-sm"
        }`}
      >
        {/* `!` — `.anticon { color: inherit }` на antd е извън Tailwind layer-ите. */}
        <CalendarOutlined className="!text-pos-accent" />
      </span>
      <div className="min-w-0 flex-1">
        <div
          title={reservation.guest_name}
          className={`truncate font-extrabold leading-tight text-pos-ink ${
            compact ? "text-[13px]" : "text-[15px]"
          }`}
        >
          {reservation.guest_name}
        </div>
        {/* На тесен екран часът, гостите и масите се пренасят до два реда,
            вместо да се режат — името горе вече е отрязано. */}
        <div
          className={`font-medium leading-tight text-pos-soft2 ${
            compact ? "line-clamp-2 text-[11px]" : "truncate text-xs"
          }`}
        >
          {t("ordering.seatedReservation")} · {details}
        </div>
        {reservation.note && (
          <div
            title={reservation.note}
            className={`flex min-w-0 items-center gap-1 font-semibold leading-tight text-pos-accent-ink ${
              compact ? "mt-0.5 text-[11px]" : "mt-[3px] text-xs"
            }`}
          >
            <MessageOutlined className="shrink-0 !text-pos-accent-ink" />
            <span className="min-w-0 truncate">{reservation.note}</span>
          </div>
        )}
      </div>
    </div>
  );
};
